import React, { useMemo, useState, useContext, useEffect } from 'react'
import { observer } from 'mobx-react'
import {useHistory, useParams} from 'react-router'
import { Link } from 'react-router-dom'
import { XIcon } from '@heroicons/react/outline'
import { topBarStore } from '../../../lib/stores/TopBarStore'
import constants from '../../../lib/constants'
import ServerError from '../../../lib/components/ServerError'
import { CASELESS_DETAIL, HOME } from '../../routing/paths'
import StoreContext from '../../providers/storesContext'
import InputWrapper from '../../components/Forms/InputWrapper'
import Button from '../../components/Button'
import CaselessSearchStore from './CaselessSearchStore'
import Spinner from '../../components/Spinner'

const CaselessSearch = () => {
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const { licenseStore } = useContext(StoreContext)
  const { location } = useParams()
  const [caselessSearchStore] = useState(() => new CaselessSearchStore())

  const options = useMemo(
    () =>
      Object.entries(constants.states).map(([code, state]) => ({
        value: code,
        label: state
      })),
    []
  )

  const handleChangeOperation = (e) => {
    if (e.target.value != '') {
      caselessSearchStore.changeOperation(e.target.value)
    }
  }
  const handleChangeLicensePlateNumber = (val) =>
    caselessSearchStore.changeLicensePlateNumber(val.toUpperCase())
  const handleChangeLicensePlateState = (e) => {
    if (e.target.value != '') {
      caselessSearchStore.changeLicensePlateState(e.target.value)
    }
  }

  const handleSearch = () => {
    caselessSearchStore.search().then((license) => {
      if (license) {
        licenseStore.setLicense(license)
        licenseStore.setOperationId(caselessSearchStore.operation.value)
        history.push(CASELESS_DETAIL)
      }
    })
  }

  const fillLocation = () => {
    const locationSelected = caselessSearchStore.operations.filter((operation) => {
      const currentLocationSlug = operation.label?.toLowerCase().replaceAll(' ','-')
      return currentLocationSlug === location?.toLowerCase()
    })[0];

    if (!locationSelected?.value) {
      return window.location.href = HOME
    }

    caselessSearchStore.changeOperation(locationSelected.value.toString());
  }

  useEffect( async () => {
    await caselessSearchStore.loadOperations().then(() => {
      if (!location) {
        return
      }

      topBarStore.setHideLogo(true)
      fillLocation()
    })

    if (!location) {
      const action = (
          <Link to={HOME}>
            <XIcon className="h-6 w-6"/>
          </Link>
      )

      topBarStore.setActionIcon(action)
    }

    setLoading(false)

    return () => {
      topBarStore.reset()
    }
  }, [])

  const { licensePlateNumber, licensePlateState, serverError, isLoading } = caselessSearchStore

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner color="text-primary-light" size={10} />
      </div>
    )
  }

  return (
    <>
    {location && caselessSearchStore.selectedOperationLogo && (
    <div className='flex flex-col items-center py-8'>
      <img src={caselessSearchStore.selectedOperationLogo} alt={caselessSearchStore.operation.value} className="h-60"/>
    </div>)}
    <form
      className="grid gap-6"
      onSubmit={(e) => {
        e.preventDefault()

        handleSearch()
      }}
    >
      <label className="flex flex-col items-start flex-grow">
        <span className="text-sm font-bold mb-1 text-gray-400">Location</span>
        <select
          className={
            caselessSearchStore.operation.error
              ? 'p-2.5 w-full rounded-md text-gray-400 bg-red-50 focus:ring-red-500'
              : 'bg-gray-50 p-2.5 w-full rounded-md text-gray-400'
          }
          style={{ outlineColor: '#D1D5DB' }}
          placeholder={"Select the operation..."}
          disabled={location}
          onChange={handleChangeOperation}
          value={caselessSearchStore.operation.value}
        >
          <option value="">{ location ? 'Loading...' : 'Select the location...'}</option>
          {caselessSearchStore.operations.map((option) => {
            return (
              <option key={`operation_${option.value}`} value={option.value}>
                {option.label}
              </option>
            )
          })}
        </select>
        {caselessSearchStore.operation.errorMessage !== '' && (
          <div className="text-red-400 text-xs pt-1">
            {caselessSearchStore.operation.errorMessage}
          </div>
        )}
      </label>
      <InputWrapper
        placeholder="Type in the license plate number..."
        onChange={handleChangeLicensePlateNumber}
        inputStore={licensePlateNumber}
        label="License plate number"
      />
      <label className="flex flex-col items-start flex-grow">
        <span className="text-sm font-bold mb-1 text-gray-400">License plate state</span>
        <select
          className={
            caselessSearchStore.licensePlateState.error
              ? 'p-2.5 w-full rounded-md text-gray-400 bg-red-50 focus:ring-red-500'
              : 'bg-gray-50 p-2.5 w-full rounded-md text-gray-400'
          }
          style={{ outlineColor: '#D1D5DB' }}
          placeholder="Select the license plate state..."
          onChange={handleChangeLicensePlateState}
          value={licensePlateState.value}
        >
          <option value="">Select the license plate state...</option>
          {options.map((option) => {
            return (
              <option key={`state_${option.value}`} value={option.value}>
                {option.label}
              </option>
            )
          })}
        </select>
        {caselessSearchStore.licensePlateState.errorMessage !== '' && (
          <div className="text-red-400 text-xs pt-1">
            {caselessSearchStore.licensePlateState.errorMessage}
          </div>
        )}
      </label>
      <Button onClick={handleSearch} isLoading={isLoading}>
        Search
      </Button>
      {serverError !== '' && <ServerError message={serverError} />}
    </form>
    </>
  )
}

export default observer(CaselessSearch)
